import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, Check, ChevronDown, Globe, Loader2 } from 'lucide-react'
import {
 DropdownMenu,
 DropdownMenuTrigger,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuLabel,
 DropdownMenuSeparator,
} from 'design-system'
import { cn } from 'design-system/lib/utils'
import { useBranch } from 'hooks/useBranch'
import { useAuth } from 'slices/user/hooks'
import { useAppDispatch } from 'slices/hooks'
import { fetchBranches, selectBranch, branchChanged } from 'slices/branchSlice'
import { branchesAPI } from 'api/branches'
import showError from 'helpers/ErrorNotification'
import showSuccess from 'helpers/SuccessNotification'
import { getUserData } from 'helpers/Auth'

const BranchSwitcher: React.FC = () => {
 const { t, i18n } = useTranslation()
 const dispatch = useAppDispatch()
 const { canUseGlobalView, isTenantWideOperator } = useAuth()
 const { selectedBranchId, selectedBranch, branches, viewMode, loading } = useBranch()

 const [open, setOpen] = useState(false)
 const [switching, setSwitching] = useState(false)
 const autoSingleBranchRef = useRef(false)

 const hasToken = Boolean(getUserData()?.token)

 useEffect(() => {
 if (hasToken) {
 dispatch(fetchBranches())
 } else {
 autoSingleBranchRef.current = false
 }
 }, [dispatch, hasToken])

 const onSelectGlobal = async () => {
 if (switching) return
 setSwitching(true)
 setOpen(false)
 try {
 const res = await branchesAPI.switchCurrent(null)
 if (res.status === 'success') {
 const raw = localStorage.getItem('authUser')
 if (raw) {
 try {
 const parsed = JSON.parse(raw) as { token?: string; user?: Record<string, unknown>; tenant_slug?: string; support_session?: Record<string, unknown> }
 const token = parsed.token
 const tenantSlug = parsed.tenant_slug ?? localStorage.getItem('tenant_slug') ?? undefined
 localStorage.setItem(
 'authUser',
 JSON.stringify({
 token,
 user: res.data?.user,
 ...(tenantSlug ? { tenant_slug: tenantSlug } : {}),
 ...(parsed.support_session ? { support_session: parsed.support_session } : {}),
 })
 )
 window.dispatchEvent(new Event('auth:updated'))
 } catch {}
 }

 dispatch(selectBranch(null))
 dispatch(branchChanged())
 showSuccess(res.message || t('Switched to Global View'))
 const shouldReload = viewMode !== 'global' || selectedBranchId != null
 if (shouldReload) {
 window.location.reload()
 return
 }
 } else {
 showError((res as { message?: string }).message || t('Failed to switch branch'))
 }
 } catch (e: unknown) {
 const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message || t('Failed to switch branch')
 showError(msg)
 } finally {
 setSwitching(false)
 }
 }

 const onSelectBranch = async (branchId: string) => {
 if (switching) return
 setSwitching(true)
 setOpen(false)
 try {
 const res = await branchesAPI.switchCurrent(branchId)
 if (res.status === 'success') {
 const raw = localStorage.getItem('authUser')
 if (raw) {
 try {
 const parsed = JSON.parse(raw) as { token?: string; user?: Record<string, unknown>; tenant_slug?: string; support_session?: Record<string, unknown> }
 const token = parsed.token
 const tenantSlug = parsed.tenant_slug ?? localStorage.getItem('tenant_slug') ?? undefined
 localStorage.setItem(
 'authUser',
 JSON.stringify({
 token,
 user: res.data?.user,
 ...(tenantSlug ? { tenant_slug: tenantSlug } : {}),
 ...(parsed.support_session ? { support_session: parsed.support_session } : {}),
 })
 )
 window.dispatchEvent(new Event('auth:updated'))
 } catch {}
 }
 dispatch(selectBranch(branchId))
 dispatch(branchChanged())
 showSuccess(res.message || t('Branch updated'))
 const shouldReload = branchId !== selectedBranchId || viewMode !== 'branch'
 if (shouldReload) {
 window.location.reload()
 return
 }
 } else {
 showError((res as { message?: string }).message || t('Failed to switch branch'))
 }
 } catch (e: unknown) {
 const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message || t('Failed to switch branch')
 showError(msg)
 } finally {
 setSwitching(false)
 }
 }

 useEffect(() => {
 if (!hasToken || loading || switching) return
 if (canUseGlobalView) return
 if (branches.length !== 1 || viewMode !== 'global') return
 if (autoSingleBranchRef.current) return
 autoSingleBranchRef.current = true
 void onSelectBranch(branches[0].id)
 // Intentionally omit onSelectBranch: stable enough for “run once when list + mode settle”
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [hasToken, loading, switching, canUseGlobalView, branches, viewMode])

 if (!hasToken) return null

 const multi =
 branches.length > 1 ||
 canUseGlobalView ||
 (branches.length >= 1 && viewMode === 'global')

 const menuItemClass = (selected: boolean) =>
 cn(
 'cursor-pointer rounded-lg px-2.5 py-2.5 text-foreground focus:bg-muted focus:text-foreground',
 selected && 'bg-primary-soft/50 dark:bg-primary-soft-strong/40',
 )

 const branchName =
 viewMode === 'global'
 ? t('All Branches')
 : selectedBranch?.name ?? t('No branch selected')

 const branchCode = viewMode === 'branch' && selectedBranch?.code ? selectedBranch.code : null

 const IconComponent = viewMode === 'global' ? Globe : Building2

 const subtitle =
 viewMode === 'global'
 ? isTenantWideOperator
 ? t('Global View')
 : t('Global view limited hint')
 : multi
 ? t('Switch branch')
 : t('Current branch')

 const pillClassName = cn(
 // Mobile: compact icon-only control so it stays visible next to status/sync chips.
 // sm+: full pill with branch name + subtitle.
 'app-header__branch-pill flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border-subtle bg-card p-0 transition-all duration-200 hover:-translate-y-px active:translate-y-0',
 'sm:h-auto sm:min-h-10 sm:w-full sm:min-w-0 sm:justify-start sm:gap-2 sm:px-3 sm:py-1.5',
 viewMode === 'global' && 'bg-primary-soft/55 sm:border-primary-soft-border/60',
 multi &&
 'cursor-pointer hover:border-primary/20 hover:bg-primary-soft/45 data-[state=open]:border-primary/25 data-[state=open]:ring-2 data-[state=open]:ring-primary/10',
 )

 const iconWrapClass = cn(
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-primary',
  // Mobile: icon sits directly in the compact pill (no nested tile).
  'border-transparent bg-transparent',
  viewMode === 'global'
    ? 'sm:border sm:border-border-subtle sm:bg-muted'
    : 'sm:border sm:border-primary-soft-border/60 sm:bg-primary-soft-strong/70',
 )

 const triggerLabel = multi ? t('Switch branch') : subtitle
 const triggerTitle = `${branchName}${branchCode ? ` (${branchCode})` : ''}`

 const renderPillBody = (withChevron: boolean) => (
 <>
 <span className={iconWrapClass} aria-hidden>
 <IconComponent className="h-4 w-4 shrink-0 sm:h-3.5 sm:w-3.5" />
 </span>
 <span className="hidden min-w-0 flex-1 flex-col items-start justify-center gap-0 text-start leading-tight sm:flex">
 <span className="flex w-full min-w-0 items-center gap-1.5">
 <span className="truncate text-xs font-semibold text-foreground sm:text-sm">{branchName}</span>
 {branchCode ? (
 <span className="hidden shrink-0 rounded-md border border-border-subtle bg-card/90 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-muted-foreground sm:inline">
 {branchCode}
 </span>
 ) : null}
 </span>
 <span className="w-full truncate text-[11px] text-muted-foreground">{subtitle}</span>
 </span>
 {withChevron ? (
 <ChevronDown
 className={cn(
 'hidden h-4 w-4 shrink-0 text-muted-foreground/80 transition-transform duration-200 sm:block',
 open && 'rotate-180',
 )}
 aria-hidden
 />
 ) : null}
 </>
 )

 return (
 <div className="header-item flex shrink-0 items-center sm:me-2 sm:min-w-0 sm:max-w-full" dir={i18n.dir()}>
 {loading ? (
 <div className={pillClassName} aria-busy="true" title={triggerTitle}>
 <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground sm:hidden" />
 <span className={cn(iconWrapClass, 'hidden sm:flex')} aria-hidden>
 <IconComponent className="h-3.5 w-3.5 shrink-0" />
 </span>
 <Loader2 className="hidden h-4 w-4 shrink-0 animate-spin text-muted-foreground sm:block" />
 </div>
 ) : multi ? (
 <DropdownMenu open={open} onOpenChange={setOpen}>
 <DropdownMenuTrigger asChild disabled={switching}>
 <button
 type="button"
 className={pillClassName}
 aria-label={triggerLabel}
 title={triggerTitle}
 >
 {renderPillBody(true)}
 </button>
 </DropdownMenuTrigger>
 <DropdownMenuContent
 align="end" sideOffset={8}
 className="w-[min(92vw,280px)] overflow-hidden rounded-xl border border-border-subtle bg-popover p-1.5 text-popover-foreground shadow-lg" dir={i18n.dir()}
 >
 <DropdownMenuLabel className="px-2.5 py-2 text-xs font-semibold text-muted-foreground">
 {t('Switch branch')}
 </DropdownMenuLabel>
 {canUseGlobalView && (
 <>
 <DropdownMenuItem
 onClick={onSelectGlobal}
 className={menuItemClass(viewMode === 'global')}
 >
 <div className="flex w-full items-center gap-3">
 <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary">
 <Globe className="h-4 w-4 text-primary"/>
 </span>
 <div className="min-w-0 flex-1">
 <p className="truncate text-sm font-medium">{t('All Branches')}</p>
 <p className="truncate text-xs text-muted-foreground">{t('Global View')}</p>
 </div>
 {viewMode === 'global' ? <Check className="h-4 w-4 shrink-0 text-primary"/> : null}
 </div>
 </DropdownMenuItem>
 {branches.length > 0 ? <DropdownMenuSeparator className="my-1 bg-border-subtle/80"/> : null}
 </>
 )}
 <div className="max-h-[min(50vh,280px)] overflow-y-auto">
 {branches.map((b) => {
 const selected = selectedBranchId === b.id && viewMode === 'branch'
 return (
 <DropdownMenuItem
 key={b.id}
 onClick={() => void onSelectBranch(b.id)}
 className={menuItemClass(selected)}
 >
 <div className="flex w-full items-center gap-3">
 <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary">
 <Building2 className="h-4 w-4 text-primary"/>
 </span>
 <div className="min-w-0 flex-1">
 <p className="truncate text-sm font-medium">{b.name}</p>
 <p className="truncate text-xs text-muted-foreground">{b.code}</p>
 </div>
 {selected ? <Check className="h-4 w-4 shrink-0 text-primary"/> : null}
 </div>
 </DropdownMenuItem>
 )
 })}
 </div>
 </DropdownMenuContent>
 </DropdownMenu>
 ) : (
 <div className={pillClassName} aria-label={subtitle} title={triggerTitle}>
 {renderPillBody(false)}
 </div>
 )}
 </div>
 )
}

export default BranchSwitcher
